import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Play, Maximize } from 'lucide-react';
import PrivateGalleryCard from './common/PrivateGalleryCard';

// Build stamp for cache debugging
const BUILD_STAMP = 'v5-2024-12-25-private-gallery';

// Debug flag - set localStorage.debug_media = '1' to enable
const isDebug = () => typeof window !== 'undefined' && localStorage.getItem('debug_media') === '1';
const debugLog = (...args) => {
  if (isDebug()) {
    console.info('[MediaGallery]', ...args);
  }
};

/**
 * Normalize media URLs to ensure they're accessible
 * Handles various URL formats that might be stored in the database
 */
const normalizeMediaUrl = (src) => {
  if (!src) return "";
  // Already absolute URL
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  // Already has /api prefix
  if (src.startsWith("/api/")) return src;
  // Missing leading slash on api path
  if (src.startsWith("api/")) return `/${src}`;
  // Old format without /api prefix - normalize to /api for proper routing
  if (src.startsWith("/uploads/videos/")) {
    return src.replace("/uploads/videos/", "/api/uploads/videos/");
  }
  if (src.startsWith("/uploads/profile_videos/")) {
    return src.replace("/uploads/profile_videos/", "/api/uploads/profile_videos/");
  }
  if (src.startsWith("/uploads/profile_photos/")) {
    return src.replace("/uploads/profile_photos/", "/api/uploads/profile_photos/");
  }
  // Handle raw filenames accidentally stored
  if (src.includes(".mp4") || src.includes(".webm") || src.includes(".mov")) {
    return `/api/uploads/videos/${src}`;
  }
  // Default: assume it's a relative path that needs /api prefix
  if (src.startsWith("/")) {
    return src;
  }
  return src;
};

/**
 * ProfileMediaGallery - Unified media gallery for photos + video + private gallery
 * 
 * Features:
 * - Clickable thumbnails with proper pointer-events
 * - Active state management with key-based re-rendering
 * - Video playback support (mp4, webm, quicktime)
 * - URL normalization for consistent playback
 * - Clear error UI when video can't play
 * - Private gallery section with consent-based access
 * - Debug mode via localStorage.debug_media = '1'
 */
export default function ProfileMediaGallery({ 
  photos = [], 
  videoUrl = null, 
  displayName = 'User',
  size = 'md',
  // --- Private gallery (consent only) ---
  ownerUserId = null,
  privatePhotos = null,
  privateVideoUrl = null,
  privateMediaCount = 0,
  privateAccessStatus = "none", // "none" | "pending" | "approved" | "denied"
  onRequestPrivateAccess = null,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);
  const videoRef = useRef(null);

  // Normalize all media into one array
  const media = useMemo(() => {
    const items = [];
    
    // Add photos - handle both string URLs and objects
    const photoList = Array.isArray(photos) ? photos : [];
    photoList.forEach((url, idx) => {
      const src = typeof url === 'string' ? url : (url?.url || url?.src || null);
      if (src) {
        items.push({ 
          type: 'image', 
          src: normalizeMediaUrl(src), 
          alt: `${displayName} photo ${idx + 1}` 
        });
      }
    });
    
    // Add video at the end if exists
    if (videoUrl) {
      const normalizedVideo = normalizeMediaUrl(videoUrl);
      items.push({ 
        type: 'video', 
        src: normalizedVideo, 
        alt: `${displayName} video intro` 
      });
      debugLog('Video added to media array:', videoUrl, '→', normalizedVideo);
    }
    
    debugLog('Media array built:', items.length, 'items', items.map(m => m.type));
    return items;
  }, [photos, videoUrl, displayName]);

  // Build private media array (only if approved)
  const privateMedia = useMemo(() => {
    const p = Array.isArray(privatePhotos) ? privatePhotos : [];
    const items = [];
    for (const src of p) {
      if (src) items.push({ type: "image", src: normalizeMediaUrl(src) });
    }
    if (privateVideoUrl) {
      items.push({ type: "video", src: normalizeMediaUrl(privateVideoUrl) });
    }
    return items;
  }, [privatePhotos, privateVideoUrl]);

  // Reset video error when switching media
  const handleThumbClick = (idx) => {
    debugLog('Thumbnail clicked:', idx, '→', media[idx]?.type);
    setVideoError(false);
    setShowPlayOverlay(true); // Reset play overlay for new video
    setActiveIndex(idx);
  };

  // Patch 2: Handle tap-to-play for mobile (user gesture required)
  const handlePlayVideo = async () => {
    const el = videoRef.current;
    if (!el) return;

    setVideoError(false);
    setShowPlayOverlay(false);

    try {
      await el.play();
    } catch (e) {
      debugLog('Play failed:', e.message);
      setVideoError(true);
    }
  };

  // Fullscreen handler with iOS Safari fallback
  const requestVideoFullscreen = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    try {
      // Standard Fullscreen API
      if (el.requestFullscreen) return await el.requestFullscreen();
      // iOS Safari fallback
      if (typeof el.webkitEnterFullscreen === "function") return el.webkitEnterFullscreen();
      // Older WebKit fallback
      if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
    } catch (e) {
      // Fail silently; controls still allow native fullscreen on most browsers
      console.warn("Fullscreen request failed:", e);
    }
  }, []);

  // If no media, show placeholder
  if (media.length === 0) {
    return (
      <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-rose-100 to-purple-100 flex items-center justify-center">
        <span className="text-4xl opacity-50">💜</span>
      </div>
    );
  }

  const activeMedia = media[activeIndex] || media[0];

  // Size variants for the main display
  const sizeClasses = {
    sm: 'max-h-48',
    md: 'max-h-72',
    lg: 'max-h-96'
  };

  // Unique key to force React to fully replace the media element when it changes
  const mediaKey = `${activeMedia.type}-${activeIndex}-${activeMedia.src?.slice(-20) || 'none'}`;

  return (
    <div className="relative z-10 space-y-3">
      {/* Build stamp - only in dev AND only when explicitly enabled */}
      {process.env.NODE_ENV !== "production" &&
        process.env.REACT_APP_SHOW_BUILD_STAMP === "1" && (
          <div className="text-[10px] text-slate-400 opacity-60 text-right">
            MediaGallery {BUILD_STAMP}
          </div>
      )}

      {/* Main Display - KEY IS CRITICAL for proper re-rendering */}
      <div className={`w-full aspect-square rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center ${sizeClasses[size]}`}>
        {activeMedia.type === 'image' ? (
          <img 
            key={mediaKey}
            src={activeMedia.src} 
            alt={activeMedia.alt}
            className="w-full h-full object-cover"
            onLoad={() => debugLog('Image loaded:', activeMedia.src?.slice(-30))}
            onError={() => debugLog('Image FAILED:', activeMedia.src?.slice(-30))}
          />
        ) : (
          <div className="w-full relative pointer-events-auto">
            <video 
              ref={videoRef}
              key={mediaKey}
              controls 
              playsInline
              preload="metadata"
              className="w-full max-h-[70vh] object-contain rounded-xl bg-black"
              poster={media.find(m => m.type === 'image')?.src}
              onLoadedMetadata={() => {
                debugLog('Video metadata loaded');
                setVideoError(false);
              }}
              onPlay={() => setShowPlayOverlay(false)}
              onError={(e) => {
                debugLog('Video FAILED:', e.target.error?.message);
                setVideoError(true);
              }}
            >
              {/* Multiple source types for browser compatibility */}
              <source src={activeMedia.src} type="video/mp4" />
              <source src={activeMedia.src} type="video/webm" />
              <source src={activeMedia.src} type="video/quicktime" />
              Your browser does not support video playback.
            </video>

            {/* Fullscreen button overlay */}
            <button
              type="button"
              onClick={requestVideoFullscreen}
              className="absolute right-3 top-3 z-20 inline-flex items-center justify-center rounded-full bg-slate-900/70 text-white hover:bg-slate-900/85 border border-white/10 shadow-lg w-11 h-11 touch-manipulation select-none pointer-events-auto"
              aria-label="Fullscreen video"
              title="Fullscreen"
            >
              <Maximize className="w-5 h-5" />
            </button>

            {/* Tap-to-play overlay for mobile (user gesture) */}
            {showPlayOverlay && !videoError && (
              <button
                type="button"
                onClick={handlePlayVideo}
                className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl transition hover:bg-black/40"
                aria-label="Play video"
              >
                <div className="rounded-full bg-white/90 px-5 py-3 text-slate-900 font-medium flex items-center gap-2 shadow-lg">
                  <Play className="w-5 h-5" />
                  <span>Tap to play</span>
                </div>
              </button>
            )}

            {/* Video error message - helpful for users */}
            {videoError && (
              <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-white/80">
                <div className="font-semibold text-white">Video couldn&apos;t play on this device.</div>
                <div className="mt-1">
                  Try uploading an <span className="font-semibold">MP4</span> (recommended). Some Android devices won&apos;t play
                  iPhone <span className="font-semibold">.mov</span> files reliably.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Thumbnails - only show if more than 1 media item */}
      {media.length > 1 && (
        <div className="relative z-[70] flex gap-2 overflow-x-auto pb-1">
          {media.map((item, idx) => (
            <button
              key={`thumb-${idx}-${item.src?.slice(-10) || idx}`}
              onClick={() => handleThumbClick(idx)}
              onTouchEnd={(e) => {
                // Extra touch handler for mobile
                e.preventDefault();
                handleThumbClick(idx);
              }}
              className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer select-none ${
                idx === activeIndex 
                  ? 'border-rose-500 ring-2 ring-rose-200 scale-105' 
                  : 'border-slate-200 hover:border-slate-300 active:scale-95'
              }`}
              aria-label={`View ${item.type === 'video' ? 'video' : `photo ${idx + 1}`}`}
              aria-pressed={idx === activeIndex}
              type="button"
            >
              {item.type === 'image' ? (
                <img 
                  src={item.src} 
                  alt={item.alt}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-slate-800 flex items-center justify-center pointer-events-none">
                  {/* Video thumbnail with play icon overlay */}
                  {media.find(m => m.type === 'image')?.src && (
                    <img 
                      src={media.find(m => m.type === 'image')?.src} 
                      alt="Video thumbnail"
                      className="absolute inset-0 w-full h-full object-cover opacity-60 pointer-events-none select-none"
                      draggable={false}
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
                      <Play className="w-4 h-4 text-slate-800 ml-0.5" />
                    </div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Media count indicator */}
      {media.length > 1 && (
        <div className="text-xs text-slate-500 text-center">
          {activeIndex + 1} / {media.length} 
          {activeMedia.type === 'video' && ' • Video'}
        </div>
      )}

      {/* --- Private gallery section (consent only; no paywall) --- */}
      {Number(privateMediaCount || 0) > 0 && (
        <div className="mt-4">
          {privateAccessStatus !== "approved" && (
            <PrivateGalleryCard
              ownerDisplayName={displayName}
              ownerUserId={ownerUserId}
              status={privateAccessStatus}
              privateMediaCount={privateMediaCount}
              onRequestAccess={onRequestPrivateAccess}
            />
          )}

          {privateAccessStatus === "approved" && privateMedia.length > 0 && (
            <div className="mt-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-200">Private gallery</div>
                <div className="text-[11px] text-emerald-400">✓ Approved</div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-3">
                <div className="grid grid-cols-3 gap-2">
                  {privateMedia.slice(0, 6).map((m, idx) => (
                    <div key={idx} className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
                      {m.type === "image" ? (
                        <img alt={`Private media ${idx + 1}`} src={m.src} className="h-24 w-full object-cover" />
                      ) : (
                        <div className="flex h-24 w-full items-center justify-center bg-slate-800 text-xs text-slate-400">
                          <Play className="w-4 h-4 mr-1" />
                          Video
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {privateMedia.length > 6 && (
                  <div className="mt-2 text-xs text-slate-500 text-center">
                    +{privateMedia.length - 6} more items
                  </div>
                )}
                <div className="mt-2 text-[11px] text-slate-600 text-center">
                  Private media is only visible after explicit approval.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
